import React, { FC, useEffect } from 'react';
import { IAssignee, ISelectorOption } from '@symfa-inc/providence-types';
import { Form } from 'antd';
import { isValidForm } from '@core/utils/methods';
import { ToDoOrRequiresAttentionsUpdateData } from '@models/interfaces';
import { AddOrEditModal } from '@shared/components';
import { Autocomplete, TextArea } from '@shared/modules';
import { EditDashboardItemItemProps } from '../models';

export const EditDashboardItem: FC<EditDashboardItemItemProps> = ({
  visible,
  closeHandler,
  saveHandler,
  editableRecord,
  adminList,
  title,
  userId,
  isAdminUser,
}: EditDashboardItemItemProps) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (editableRecord) {
      const {
        notificationData: { assignees, taskNote },
      } = editableRecord;

      form.setFieldsValue({
        taskNote,
        assignees: assignees.map(({ assignee }: IAssignee) => assignee),
      });
    }
  }, [editableRecord, form]);

  const onCancel = (): void => {
    form.resetFields();
    closeHandler();
  };

  const onOk = async (): Promise<void> => {
    if (await isValidForm(form)) {
      saveHandler({
        _id: editableRecord!._id,
        reporter: userId,
        ...form.getFieldsValue(),
      } as ToDoOrRequiresAttentionsUpdateData);
      onCancel();
    }
  };

  return (
    <AddOrEditModal
      title={title}
      visible={visible}
      onOk={onOk}
      onCancel={onCancel}
      formProps={{
        labelCol: { span: 6 },
        wrapperCol: { span: 15 },
        form,
      }}
      cancelText="Cancel"
      cancelButtonProps={{
        onClick: onCancel,
      }}
    >
      <Autocomplete
        label="Assigners"
        id="assignees"
        options={adminList.map(({ value, viewValue }: ISelectorOption) => ({
          value,
          viewValue,
          disabled: isAdminUser
            ? userId === value
            : !!editableRecord?.notificationData.assignees.find(
                ({ reporter, assignee }: IAssignee) =>
                  userId !== reporter && value === assignee,
              ),
        }))}
        elementProps={{
          mode: 'multiple',
        }}
      />
      <TextArea label="Task Note" id="taskNote" />
    </AddOrEditModal>
  );
};
